import React, {useCallback, useMemo} from 'react';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {designSystemToken, Label} from '@lightricks/react-design-system';
import VideoDisplay, {Control} from '@contentlab/components/video-display';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useViewDismissedEvent from '@contentlab/hooks/analytics/use-view-dismissed-event';
import useContentLabPageName from '@contentlab/hooks/use-content-lab-page-name';
import contentLabStore, {
  contentLabActions,
} from '@contentlab/stores/contentLabStore';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import ModalNavigation from '@/components/modal-navigation';
import useAssetQuery from '@/hooks/content-studio/use-asset-query';

function AssetsPreviewModal() {
  const {previewAssets, activePreviewAssetId} = contentLabStore(
    useShallow((state) => ({
      previewAssets: state.previewAssets,
      activePreviewAssetId: state.activePreviewAssetId,
    }))
  );
  const pageName = useContentLabPageName();
  const {uiInteraction} = useUiInteractionEvent();
  const {viewDismissed} = useViewDismissedEvent();
  const activePreviewAsset = useMemo(() => {
    return previewAssets.find(
      (previewAsset) => previewAsset.assetId === activePreviewAssetId
    );
  }, [previewAssets, activePreviewAssetId]);

  const {data: assetData} = useAssetQuery({
    id: activePreviewAsset?.assetId || '',
    enabled: !activePreviewAsset?.file,
  });

  const title = useMemo(() => {
    const current = previewAssets.findIndex(
      (previewAsset) => previewAsset.assetId === activePreviewAssetId
    );
    return (
      <Label size="xl" color={designSystemToken('semantic.fg.inverse')}>
        {current + 1} {translate('out-of')} {previewAssets.length}
      </Label>
    );
  }, [previewAssets, activePreviewAssetId]);

  const navigatePreviewAsset = (direction: 'next' | 'previous') => {
    const currentIndex = previewAssets.findIndex(
      (previewAsset) => previewAsset.assetId === activePreviewAssetId
    );

    if (currentIndex === -1) {
      return; // No active asset found
    }
    uiInteraction({
      item_name: `navigate_preview_asset_${direction}`,
      interaction_location: CONTENT_LAB_VIEW_NAMES.PREVIEW_ASSET,
      flow_name: pageName,
      asset_id: currentIndex.toString(),
    });
    let newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;

    // Wrap around if we reach the end or start
    if (newIndex < 0) {
      newIndex = previewAssets.length - 1;
    } else if (newIndex >= previewAssets.length) {
      newIndex = 0;
    }

    contentLabActions.setActivePreviewAssetId(previewAssets[newIndex].assetId);
  };

  const generateVideoFileUrl = useCallback(() => {
    if (activePreviewAsset?.file) {
      return URL.createObjectURL(activePreviewAsset.file);
    }
    return '';
  }, [activePreviewAsset]);

  const videoProps = useMemo(() => {
    if (!activePreviewAsset) {
      return null;
    }

    return {
      key: activePreviewAsset?.assetId || '',
      videoName: activePreviewAsset?.fileName || assetData?.name || '',
      videoUrl: generateVideoFileUrl() || assetData?.url || '',
      subtitleTime: 0,
      onPlayToggle: (isPlaying: boolean) => {
        uiInteraction({
          item_name: isPlaying ? 'play_preview_asset' : 'pause_preview_asset',
          interaction_location: CONTENT_LAB_VIEW_NAMES.PREVIEW_ASSET,
          flow_name: pageName,
          asset_id: activePreviewAssetId?.toString() || '',
        });
      },
    };
  }, [activePreviewAsset, assetData, generateVideoFileUrl]);

  return (
    <ModalNavigation
      open={previewAssets.length > 0}
      onClose={() => {
        const interactionId = uuid();
        const presentationId = AnalyticsService.getFlow(
          FLOW_NAMES.CONTENT_LAB.ASSETS_PREVIEW_MODAL
        ).screen_presentation_id;
        uiInteraction({
          item_name: 'close_preview_asset',
          interaction_location: CONTENT_LAB_VIEW_NAMES.PREVIEW_ASSET,
          flow_name: pageName,
          asset_id: activePreviewAssetId?.toString() || '',
        });

        viewDismissed({
          interaction_id: interactionId,
          presentation_id: presentationId,
          reason: 'cancel',
          flow_name: pageName,
        });
        contentLabActions.setPreviewAssets([]);
        contentLabActions.setActivePreviewAssetId(null);
      }}
      onNext={() => navigatePreviewAsset('next')}
      onPrevious={() => navigatePreviewAsset('previous')}
      title={title}
    >
      {videoProps ? (
        <VideoDisplay
          {...videoProps}
          enableKeyboardControls
          controls={[Control.VOLUME]}
        />
      ) : null}
    </ModalNavigation>
  );
}

export default AssetsPreviewModal;
